import type React from "react";
import { Box, Icon, Text, Tooltip, TooltipPopup, TooltipTrigger } from "@salestrak/ui";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { Omittable } from "@salestrak/ui";

type PermissionGateProps = Omittable<{
  perm: string;
  hideOnNoAccess?: boolean;
  message?: string;
  children: React.ReactNode;
}>;

/**
 * Wraps children with permission-based access control.
 *
 * - If user has permission → renders children normally
 * - If no permission + hideOnNoAccess → renders nothing
 * - If no permission + !hideOnNoAccess → renders children disabled with tooltip
 */
export function PermissionGate({
  perm,
  hideOnNoAccess = false,
  message = "You do not have permission to perform this action",
  children,
}: PermissionGateProps) {
  const hasAccess = usePermission(perm);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (hideOnNoAccess) {
    return null;
  }

  return (
    <Tooltip>
      {/* The trigger must stay pointer-reachable for the tooltip to fire, so
          only the gated children are inert. */}
      <TooltipTrigger render={<Box opacity={50} />}>
        <Box pointerEvents="none">{children}</Box>
      </TooltipTrigger>
      <TooltipPopup side="top">
        <Box direction="row" align="center" gap="sm">
          <Icon name="shield" size="md" />
          <Text size="xs">{message}</Text>
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}
